import { useRef, useCallback, useEffect, useState } from "react";

const AiCorner = ({ activeVideo, videoData, onVideoLoaded, backgroundUrl, posterSrc, videoDebugInfo }) => {
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [currentIdleType, setCurrentIdleType] = useState('smile');
  const idleTimerRef = useRef(null);

  // 清除待機模式計時器
  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  // 設置待機模式計時器
  const setupIdleTimer = useCallback(() => {
    clearIdleTimer();
    const randomDelay = Math.random() * 5000 + 10000;

    idleTimerRef.current = setTimeout(() => {
      if (activeVideo === 'aiStart' || activeVideo === 'aiPending') {
        setCurrentIdleType(prevType =>
          prevType === 'smile' && Math.random() < 0.3 ? 'wave' : 'smile'
        );
        setupIdleTimer();
      }
    }, randomDelay);
  }, [activeVideo, clearIdleTimer]);

  // 獲取當前應該播放的影片 URL
  const getCurrentVideoUrl = useCallback(() => {
    if (!videoData) return null;

    switch (activeVideo) {
      case 'aiTalking':
        return videoData.talking;
      case 'aiStart':
      case 'aiPending':
        return videoData.idle[currentIdleType];
      default:
        return videoData.idle?.smile;
    }
  }, [activeVideo, videoData, currentIdleType]);

  // 視頻播放函數
  const playVideo = useCallback(async (video) => {
    try {
      video.muted = true;
      video.volume = 0;
      await video.play();
    } catch (err) {
      const handleUserInteraction = () => {
        video.play().catch(e => console.log('[AiCorner] 用戶互動後播放失敗:', e));
        document.removeEventListener('click', handleUserInteraction);
        document.removeEventListener('touchstart', handleUserInteraction);
      };

      document.addEventListener('click', handleUserInteraction, { once: true });
      document.addEventListener('touchstart', handleUserInteraction, { once: true });
    }
  }, []);

  // 視頻重播函數
  const replayVideo = useCallback((video) => {
    video.currentTime = 0;
    video.play().catch(e => console.log('[AiCorner] 重播失敗:', e));
  }, []);

  const currentVideoUrl = getCurrentVideoUrl();

  // 處理 activeVideo 狀態變化和視頻載入
  useEffect(() => {
    // 處理待機模式
    if (activeVideo === 'aiTalking') {
      clearIdleTimer();
      setCurrentIdleType('smile');
    } else if (activeVideo === 'aiStart' || activeVideo === 'aiPending') {
      setupIdleTimer();
    }

    // 處理視頻載入
    const video = videoRef.current;
    if (!video || !currentVideoUrl) return;

    setIsVideoLoaded(false);
    if (onVideoLoaded) onVideoLoaded(false);

    // 重新載入視頻
    video.pause();
    video.currentTime = 0;
    video.src = currentVideoUrl;
    video.muted = true;
    video.volume = 0;
    video.load();

    const handleLoadedMetadata = () => playVideo(video);
    const handleVideoEnded = () => replayVideo(video);
    const handleCanPlay = () => {
      setIsVideoLoaded(true);
      if (onVideoLoaded) onVideoLoaded(true);
    };
    const handleError = (e) => {
      console.error('[AiCorner] 視頻播放錯誤:', e);
      setIsVideoLoaded(false);
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("ended", handleVideoEnded);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("error", handleError);

    return () => {
      clearIdleTimer();
      video?.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video?.removeEventListener("ended", handleVideoEnded);
      video?.removeEventListener("canplay", handleCanPlay);
      video?.removeEventListener("error", handleError);
    };
  }, [activeVideo, currentVideoUrl, setupIdleTimer, clearIdleTimer, playVideo, replayVideo, onVideoLoaded]);

  return (
    <div className="absolute inset-0 z-0 flex items-start justify-center">
      {/* 背景圖片層 */}
      {backgroundUrl && (
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${backgroundUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
      )}

      <div className="relative z-10 flex h-full w-full flex-col items-center justify-center">
        <video
          ref={videoRef}
          src={currentVideoUrl}
          poster={posterSrc}
          autoPlay
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 h-full w-full object-cover"
          controls={false}
          webkit-playsinline="true"
          x5-playsinline="true"
          data-x5-playsinline="true"
          data-x5-video-player-type="h5"
          x5-video-player-fullscreen="false"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
        />

        {/* 調試信息 (可選，用於開發時查看狀態) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white text-xs p-2 rounded max-w-xs">
            <div>模式: {activeVideo}</div>
            <div>待機類型: {currentIdleType}</div>
            <div>當前影片: {currentVideoUrl}</div>
            {videoDebugInfo && currentVideoUrl && (
              <div className="text-cyan-300">
                當前影片ID: {(() => {
                  // 根據當前影片 URL 找到對應的 ID
                  for (const [actionType, url] of Object.entries(videoDebugInfo.videosByActionType || {})) {
                    if (url === currentVideoUrl) {
                      const selectedVideo = videoDebugInfo.selectedVideoIds?.find(v => v.actionType === actionType);
                      const createdVideo = videoDebugInfo.createdVideoIds?.find(v => v.actionType === actionType);
                      const currentVideoId = selectedVideo?.fileId || createdVideo?.fileId;
                      return currentVideoId ? `${currentVideoId}... (${actionType})` : '未知';
                    }
                  }
                  return '未找到';
                })()}
              </div>
            )}
            {videoDebugInfo && (
              <>
                <div className="mt-2 border-t border-gray-400 pt-2">
                  <div className="font-bold">影片映射:</div>
                  {videoDebugInfo.videosByActionType && Object.entries(videoDebugInfo.videosByActionType).map(([actionType, url]) => {
                    // 尋找對應的影片 ID
                    const selectedVideo = videoDebugInfo.selectedVideoIds?.find(v => v.actionType === actionType);
                    const createdVideo = videoDebugInfo.createdVideoIds?.find(v => v.actionType === actionType);
                    const currentVideoId = selectedVideo?.fileId || createdVideo?.fileId;
                    const isSelected = !!selectedVideo;

                    return (
                      <div key={actionType} className="ml-2 mb-1">
                        <div className="text-yellow-300">{actionType}:</div>
                        <div className="text-xs break-all">{url}</div>
                        {currentVideoId && (
                          <div className={isSelected ? "text-green-300" : "text-orange-300"}>
                            {isSelected ? "選中" : "備用"} ID: {currentVideoId}...
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiCorner;
